import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';

const WARNING_AFTER_MS = 10 * 60 * 1000;
const LOGOUT_AFTER_MS = 15 * 60 * 1000;
const COUNTDOWN_SECONDS = 5 * 60;

function IdleTimeout({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const navigate = useNavigate();
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningStartTimeRef = useRef(null);

  const clearAllTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) cancelAnimationFrame(countdownIntervalRef.current);
  };

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    try {
      await signOut();
    } catch {
      // Ignore
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    warningStartTimeRef.current = Date.now();
    setSecondsLeft(COUNTDOWN_SECONDS);

    const tick = () => {
      const elapsed = Math.floor((Date.now() - warningStartTimeRef.current) / 1000);
      const remaining = COUNTDOWN_SECONDS - elapsed;
      if (remaining <= 0) {
        setSecondsLeft(0);
        handleLogout();
      } else {
        setSecondsLeft(remaining);
        countdownIntervalRef.current = requestAnimationFrame(tick);
      }
    };

    countdownIntervalRef.current = requestAnimationFrame(tick);
  }, [handleLogout]);

  const resetTimers = useCallback(() => {
    if (showWarning) return;
    lastActivityRef.current = Date.now();
    clearAllTimers();
    warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS);
  }, [showWarning, startWarningCountdown]);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    clearAllTimers();
    lastActivityRef.current = Date.now();
    warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();

        if (showWarning && warningStartTimeRef.current) {
          const elapsed = Math.floor((now - warningStartTimeRef.current) / 1000);
          const remaining = COUNTDOWN_SECONDS - elapsed;
          if (remaining <= 0) {
            handleLogout();
          } else {
            setSecondsLeft(remaining);
          }
        } else {
          const idleTime = now - lastActivityRef.current;
          if (idleTime >= LOGOUT_AFTER_MS) {
            handleLogout();
          } else if (idleTime >= WARNING_AFTER_MS) {
            clearAllTimers();
            startWarningCountdown();
          } else {
            clearAllTimers();
            warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS - idleTime);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [showWarning, handleLogout, startWarningCountdown]);

  useEffect(() => {
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimers);
    });
    warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS);
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimers);
      });
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      {showWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            width: '400px',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#1a365d', marginBottom: '12px' }}>
              Are you still there?
            </h2>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              You've been inactive for a while. For security, you'll be logged out automatically in:
            </p>
            <p style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#e53e3e',
              margin: '16px 0'
            }}>
              {formatTime(secondsLeft)}
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#1a365d',
                  border: '1px solid #1a365d',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Log Out Now
              </button>
              <button
                onClick={handleStayLoggedIn}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#1a365d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default IdleTimeout;